import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { AlignSelfProps, DisplayProps, FontWeightProps, SizeProps } from 'styled-system';
import {
  alignItems,
  alignSelf,
  color,
  display,
  flexDirection,
  fontWeight,
  justifyContent,
  overflow,
  size,
  space,
  themeGet,
} from 'styled-system';

import type { ItemPriceV3Fragment } from '../../gql/generated/schema';
import { useLocalization } from '../../providers/LocaleProvider';
import type { ColorProps, Space, SpaceProps } from '../../theme/system-types';
import type { ArticleImage } from '../../types/Article';
import DeliveryTimePeriod from '../DeliveryTimePeriod';
import EnergyClass from '../EnergyClass';
import type { FlexProps } from '../Flex';
import { Flex } from '../Flex';
import ArticleSmallPrice, { RenderingStrategy } from '../Price/ArticleSmallPrice';
import RRPPrice from '../Price/RRPPrice';
import ResponsiveImage from '../ResponsiveImage';
import type { CommonTypographyProps } from '../Typography';
import {
  BodyRegularBR2,
  CaptionStrikeCS1,
  HeadlineH8,
  HeadlineH9,
  SubtitleST4,
} from '../Typography';
import type { Delivery, EnergyClassProps, Prices } from './types';

export const enum ArticlePreviewVariant {
  Default = 'default',
  ShopTheLook = 'shopTheLook',
}

interface ArticlePreviewProps {
  name: string;
  variantName: string;
  prices: Prices;
  images: ArticleImage[];
  delivery: Nullable<Delivery>;
  energyClass?: Nullable<EnergyClassProps>;
  itemPrice_v3?: Pick<ItemPriceV3Fragment, 'original' | 'final'>;
  imageSizes?: number[];
  showFastDelivery?: boolean;
  variant?: ArticlePreviewVariant;
}

const VARIANT_STYLES = {
  [ArticlePreviewVariant.Default]: {
    containerMx: ['sp_16', null, 'sp_24', 'sp_32'] as Space,
    containerMy: 'sp_8' as Space,
    thumbnailMr: 'sp_16' as Space,
    priceMb: ['sp_8', null, null, 'sp_12'] as Space,
    priceComponent: HeadlineH9,
  },
  [ArticlePreviewVariant.ShopTheLook]: {
    containerMx: 'sp_4' as Space,
    containerMy: 'sp_4' as Space,
    thumbnailMr: 'sp_12' as Space,
    priceMb: 'sp_0' as Space,
    priceComponent: HeadlineH8,
  },
};

interface ArticlePreviewPriceProps {
  energyClass?: Nullable<EnergyClassProps>;
  prices: Prices;
  itemPrice_v3: Pick<ItemPriceV3Fragment, 'original' | 'final'>;
  imageSizes?: number[];
}

const IMAGE_SIZES_DEFAULT = [80, 104, 120, 176];

const PRICE_COLOR_DEFAULT = 'sys.neutral.text.strong';
const PRICE_COLOR_ON_SALE = 'sys.primary.text.default';

const UnstyledArticlePreviewContainer = styled.div<any>`
  ${display};
  ${space};
`;

const ArticlePreviewContainer = (props: ComponentProps<typeof UnstyledArticlePreviewContainer>) => (
  <UnstyledArticlePreviewContainer display="flex" {...props} />
);

const UnstyledArticleThumbnail = styled(ResponsiveImage)<
  SpaceProps & SizeProps & AlignSelfProps & DisplayProps
>`
  ${space};
  ${size};
  ${alignSelf};
  ${display};
`;

const ArticleThumbnail = (props: ComponentProps<typeof UnstyledArticleThumbnail>) => (
  <UnstyledArticleThumbnail
    mr="sp_16"
    size={props.widths}
    alignSelf="flex-start"
    display="block"
    {...props}
  />
);

const UnstyledArticleDetails = styled.div<any>`
  ${display};
  ${flexDirection};
  ${justifyContent};
  ${overflow};
`;

const ArticleDetails = (props: ComponentProps<typeof UnstyledArticleDetails>) => (
  <UnstyledArticleDetails
    display="flex"
    flexDirection="column"
    justifyContent="center"
    overflow="hidden"
    {...props}
  />
);

const UnstyledArticlePriceContainer = styled(BodyRegularBR2)<any>`
  ${display};
  ${alignItems};
`;

const ArticlePriceContainer = (props: ComponentProps<typeof UnstyledArticlePriceContainer>) => (
  <UnstyledArticlePriceContainer display="flex" alignItems="center" {...props} />
);

const articleNameStyles: CommonTypographyProps = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const UnstyledEnergyClassWrapper = styled.span<any>`
  ${space};
`;

const EnergyClassWrapper = (props: ComponentProps<typeof UnstyledEnergyClassWrapper>) => (
  <UnstyledEnergyClassWrapper mr={['sp_4', null, 'sp_8']} {...props} />
);

const ArticleCurrentPrice = styled.span<ColorProps & SpaceProps & FontWeightProps>`
  ${color};
  ${space};
  ${fontWeight};
`;

const articleCurrentPriceStyles: ComponentProps<typeof ArticleCurrentPrice> = {
  pr: 'sp_4',
  fontWeight: 'bold',
};

const InnerPriceContainer = styled(Flex)<FlexProps>``;

const innerPriceContainerStyles: ComponentProps<typeof Flex> = {
  flexWrap: 'wrap',
  alignItems: 'center',
};

const articleRegularPriceStyles: CommonTypographyProps = {
  as: 'span',
  color: PRICE_COLOR_DEFAULT,
  ml: ['sp_4', null, 'sp_8'],
};

const UnstyledStyledDeliveryTimePeriod = styled(DeliveryTimePeriod)<any>`
  ${color};
  ${props =>
    props.fastDelivery &&
    css`
      color: ${themeGet('colors.sys.success.text.default')(props)};
    `};
`;

const StyledDeliveryTimePeriod = (
  props: ComponentProps<typeof UnstyledStyledDeliveryTimePeriod>
) => (
  <UnstyledStyledDeliveryTimePeriod color="sys.neutral.background.inverted" mt="sp_4" {...props} />
);

const StyledArticleSmallPrice = styled(ArticleSmallPrice)<SpaceProps>`
  ${space};
`;

const ArticlePreviewPrice = ({
  energyClass,
  prices: { rrp },
  itemPrice_v3,
}: ArticlePreviewPriceProps) => {
  const { fc } = useLocalization();
  const isOnSale = !!itemPrice_v3?.original;

  const shouldDisplayRRP = !isOnSale && !!rrp;
  const currentPrice = itemPrice_v3.final;
  const currentPriceColor = isOnSale ? PRICE_COLOR_ON_SALE : PRICE_COLOR_DEFAULT;

  return (
    <ArticlePriceContainer>
      {!!energyClass && (
        <EnergyClassWrapper>
          <EnergyClass {...energyClass} />
        </EnergyClassWrapper>
      )}
      <InnerPriceContainer {...innerPriceContainerStyles}>
        <ArticleCurrentPrice
          {...articleCurrentPriceStyles}
          data-testid="article-current-price"
          color={currentPriceColor}
        >
          {fc(currentPrice)}
        </ArticleCurrentPrice>
        {isOnSale && (
          <CaptionStrikeCS1 data-testid="article-regular-price" {...articleRegularPriceStyles}>
            {fc(itemPrice_v3.original)}
          </CaptionStrikeCS1>
        )}
        {shouldDisplayRRP && <RRPPrice rrpPrice={rrp} Typography={BodyRegularBR2} />}
      </InnerPriceContainer>
    </ArticlePriceContainer>
  );
};

const ArticlePreview = ({
  name,
  variantName,
  images,
  energyClass,
  prices,
  itemPrice_v3,
  delivery,
  showFastDelivery,
  imageSizes = IMAGE_SIZES_DEFAULT,
  variant = ArticlePreviewVariant.Default,
}: ArticlePreviewProps) => {
  const { t } = useLocalization();
  const styles = VARIANT_STYLES[variant];

  return (
    <ArticlePreviewContainer
      data-testid="article-preview"
      mx={styles.containerMx}
      my={styles.containerMy}
    >
      <ArticleThumbnail
        src={images[0].path}
        widths={imageSizes}
        mr={styles.thumbnailMr}
        alt={name}
      />
      <ArticleDetails>
        <SubtitleST4 {...articleNameStyles}>
          {name} {variantName}
        </SubtitleST4>
        {itemPrice_v3 ? (
          <ArticlePreviewPrice
            energyClass={energyClass}
            prices={prices}
            itemPrice_v3={itemPrice_v3}
          />
        ) : (
          <StyledArticleSmallPrice
            mb={styles.priceMb}
            energyClass={energyClass}
            specialPrice={prices.special}
            rrpPrice={prices.rrp}
            regularPrice={prices.regular}
            renderingStrategy={RenderingStrategy.Horizontal}
            priceComponent={styles.priceComponent}
          />
        )}
        {showFastDelivery ? (
          <SubtitleST4 color="sys.success.text.default">
            {t('h24_immediately_available')}
          </SubtitleST4>
        ) : (
          <StyledDeliveryTimePeriod {...delivery} />
        )}
      </ArticleDetails>
    </ArticlePreviewContainer>
  );
};

export default ArticlePreview;
